import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import { formatDateTimeInput } from '../../utils/dateUtils';

const COPY = {
  ar: {
    title: 'النسخ الاحتياطي',
    subtitle: 'إنشاء نسخة SQL قابلة للحفظ من قاعدة بيانات النظام مع اختيار الجداول المطلوبة.',
    refresh: 'تحديث الجداول',
    addTable: 'إضافة جدول',
    chooseTable: 'اختر جدول',
    selectAll: 'تحديد الكل',
    clear: 'مسح الاختيار',
    download: 'تنزيل النسخة',
    selectedTables: 'الجداول المختارة',
    availableTables: 'الجداول المتاحة',
    includeSchema: 'تضمين بنية الجداول',
    includeData: 'تضمين البيانات',
    addDropStatements: 'إضافة أوامر حذف قبل الإنشاء',
    table: 'الجدول',
    rows: 'صفوف تقريبية',
    size: 'الحجم MB',
    engine: 'المحرك',
    collation: 'الترميز',
    noTables: 'لا توجد جداول محملة.',
    emptySelection: 'اختر جدولا واحدا على الأقل قبل التنزيل.',
    saved: 'تم إنشاء ملف النسخة الاحتياطية.',
    adminOnly: 'هذه الخدمة مخصصة لمدير النظام فقط.',
  },
  en: {
    title: 'Database Backup',
    subtitle: 'Create a downloadable SQL backup from the system database with selected tables.',
    refresh: 'Refresh tables',
    addTable: 'Add table',
    chooseTable: 'Choose table',
    selectAll: 'Select all',
    clear: 'Clear',
    download: 'Download backup',
    selectedTables: 'Selected tables',
    availableTables: 'Available tables',
    includeSchema: 'Include table schema',
    includeData: 'Include data',
    addDropStatements: 'Add drop statements',
    table: 'Table',
    rows: 'Approx. rows',
    size: 'Size MB',
    engine: 'Engine',
    collation: 'Collation',
    noTables: 'No tables loaded.',
    emptySelection: 'Select at least one table before downloading.',
    saved: 'Backup file was created.',
    adminOnly: 'This service is restricted to system administrators.',
  },
};

function parseFilename(disposition) {
  const match = String(disposition || '').match(/filename="?([^"]+)"?/i);
  return match?.[1] || `smart-stable-backup-${formatDateTimeInput(new Date()).replace(/[:.]/g, '-')}.sql`;
}

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString('en-US') : '0';
}

export default function DatabaseBackup() {
  const { i18n } = useTranslation();
  const text = COPY[i18n.language === 'ar' ? 'ar' : 'en'];
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableToAdd, setTableToAdd] = useState(null);
  const [includeSchema, setIncludeSchema] = useState(true);
  const [includeData, setIncludeData] = useState(true);
  const [addDropStatements, setAddDropStatements] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const selectedSet = useMemo(() => new Set(selectedTables), [selectedTables]);
  const selectedRows = useMemo(
    () => tables.filter((table) => selectedSet.has(table.table_name)),
    [selectedSet, tables]
  );
  const availableToAdd = useMemo(
    () => tables.filter((table) => !selectedSet.has(table.table_name)),
    [selectedSet, tables]
  );
  const totalRows = useMemo(
    () => selectedRows.reduce((sum, table) => sum + Number(table.table_rows || 0), 0),
    [selectedRows]
  );

  const loadTables = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/backups/tables');
      const rows = Array.isArray(data?.tables) ? data.tables : [];
      setTables(rows);
      setSelectedTables((current) =>
        current.length ? current.filter((name) => rows.some((table) => table.table_name === name)) : rows.map((table) => table.table_name)
      );
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.details || err?.message || text.adminOnly);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addSelectedTable = () => {
    if (!tableToAdd?.table_name) return;
    setSelectedTables((current) =>
      current.includes(tableToAdd.table_name) ? current : [...current, tableToAdd.table_name]
    );
    setTableToAdd(null);
  };

  const toggleTable = (tableName, checked) => {
    setSelectedTables((current) => {
      if (checked) return current.includes(tableName) ? current : [...current, tableName];
      return current.filter((name) => name !== tableName);
    });
  };

  const downloadBackup = async () => {
    setError('');
    setMessage('');
    if (!selectedTables.length) {
      setError(text.emptySelection);
      return;
    }

    setDownloading(true);
    try {
      const response = await api.post(
        '/admin/backups/export',
        {
          tables: selectedTables,
          includeSchema,
          includeData,
          addDropStatements,
        },
        { responseType: 'blob', timeout: 120000 }
      );
      const filename = parseFilename(response.headers?.['content-disposition']);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/sql;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage(text.saved);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.details || err?.message || text.adminOnly);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box className="smart-page-container" sx={{ minHeight: '100vh', bgcolor: COLORS.page, maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<RefreshIcon />} onClick={loadTables} disabled={loading} sx={{ color: COLORS.gold, fontWeight: 900 }}>
              {text.refresh}
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={downloadBackup}
              disabled={downloading || loading}
              sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
            >
              {text.download}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Metric icon={<StorageIcon />} label={text.availableTables} value={formatNumber(tables.length)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<TableRowsIcon />} label={text.selectedTables} value={formatNumber(selectedTables.length)} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<TableRowsIcon />} label={text.rows} value={formatNumber(totalRows)} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={availableToAdd}
              value={tableToAdd}
              onChange={(_, value) => setTableToAdd(value)}
              getOptionLabel={(option) => option?.table_name || ''}
              renderInput={(params) => <TextField {...params} label={text.chooseTable} size="small" />}
            />
          </Grid>
          <Grid item xs={12} md="auto">
            <Button startIcon={<AddIcon />} variant="outlined" onClick={addSelectedTable} sx={{ borderColor: COLORS.gold, color: COLORS.gold, fontWeight: 900 }}>
              {text.addTable}
            </Button>
          </Grid>
          <Grid item xs={12} md>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
              <Button onClick={() => setSelectedTables(tables.map((table) => table.table_name))} sx={{ color: COLORS.green, fontWeight: 900 }}>{text.selectAll}</Button>
              <Button onClick={() => setSelectedTables([])} sx={{ color: COLORS.rose, fontWeight: 900 }}>{text.clear}</Button>
            </Stack>
          </Grid>
        </Grid>

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
          <FormControlLabel control={<Checkbox checked={includeSchema} onChange={(event) => setIncludeSchema(event.target.checked)} />} label={text.includeSchema} />
          <FormControlLabel control={<Checkbox checked={includeData} onChange={(event) => setIncludeData(event.target.checked)} />} label={text.includeData} />
          <FormControlLabel control={<Checkbox checked={addDropStatements} onChange={(event) => setAddDropStatements(event.target.checked)} />} label={text.addDropStatements} />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.availableTables}</Typography>
          <Chip label={`${selectedTables.length}/${tables.length}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>

        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{text.table}</TableCell>
                <TableCell>{text.rows}</TableCell>
                <TableCell>{text.size}</TableCell>
                <TableCell>{text.engine}</TableCell>
                <TableCell>{text.collation}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tables.map((table) => (
                <TableRow key={table.table_name} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSet.has(table.table_name)}
                      onChange={(event) => toggleTable(table.table_name, event.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900 }}>{table.table_name}</TableCell>
                  <TableCell>{formatNumber(table.table_rows)}</TableCell>
                  <TableCell>{formatNumber(table.size_mb)}</TableCell>
                  <TableCell>{table.engine || '-'}</TableCell>
                  <TableCell>{table.table_collation || '-'}</TableCell>
                </TableRow>
              ))}
              {!tables.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>{text.noTables}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
